import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, ChevronRight, Download, Loader2, MessageSquare } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import { Card, Chip, PageHeader, Button } from "../components/Primitives";
import { myProposals, findChatForRequest } from "../lib/db";

const STATUS_STYLES = {
  sent:        "!bg-electric-500/10 !text-electric-600 !border-electric-500/30",
  pending:     "!bg-electric-500/10 !text-electric-600 !border-electric-500/30",
  viewed:      "!bg-carbon-700 !text-ink-800 !border-carbon-600",
  negotiating: "!bg-amber-500/10 !text-amber-500 !border-amber-500/30",
  accepted:    "!bg-status-available/10 !text-status-available !border-status-available/30",
  declined:    "!bg-red-500/10 !text-red-400 !border-red-500/30",
  withdrawn:   "!bg-carbon-700 !text-ink-500 !border-carbon-600",
  completed:   "!bg-electric-600/10 !text-electric-600 !border-electric-600/30",
};

function toCSV(rows) {
  const header = ["proposal_id","status","request_ref","request_title","proposed_price","proposed_days","last_activity"];
  const lines = [header.join(",")];
  rows.forEach((p) => {
    const cells = [p.id, p.status, p.request?.refCode || "", p.request?.title || "", p.proposedPrice ?? "", p.proposedDays ?? "", p.when];
    lines.push(cells.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
  });
  return lines.join("\n");
}

function downloadCSV(name, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function MyProposals() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!user?.id) { setLoading(false); return; }
    myProposals(user.id).then((r) => { if (alive) { setRows(r); setLoading(false); } }).catch(() => setLoading(false));
    return () => { alive = false; };
  }, [user?.id]);

  const openChat = async (p) => {
    if (!p.request?.id || !user?.id) return;
    const chatId = await findChatForRequest(user.id, p.request.id, p.request.ownerId);
    if (chatId) navigate(`/app/messages?chat=${chatId}`);
    else navigate("/app/messages");
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto animate-fade-up" data-testid="my-proposals-page">
      <PageHeader
        kicker={<>R4Pro · Workspace</>}
        title={t("myProposals.title")}
        subtitle={t("myProposals.subtitle")}
        testId="my-proposals-header"
        right={
          <Button variant="outline" onClick={() => downloadCSV("r4pro-my-proposals.csv", toCSV(rows))} disabled={rows.length === 0} data-testid="my-proposals-export">
            <Download className="w-4 h-4" /> {t("common.exportCsv")}
          </Button>
        }
      />

      {loading ? (
        <div className="text-ink-500 font-mono text-xs py-8 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
      ) : rows.length === 0 ? (
        <Card className="p-10 text-center" data-testid="my-proposals-empty">
          <Send className="w-6 h-6 mx-auto text-ink-500 mb-2" />
          <p className="text-ink-700 text-sm">{t("myProposals.emptyTitle")}</p>
          <p className="text-ink-500 text-xs mt-1">{t("myProposals.emptyHelp")}</p>
          <Button className="mt-4" onClick={() => navigate("/app/requests")} data-testid="my-proposals-empty-cta">
            {t("myProposals.exploreCta")}
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {rows.map((p) => (
            <Card key={p.id} data-testid={`my-prop-${p.id}`} className="p-5 hover:border-electric-500/40 transition">
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono text-[10px] text-electric-600">{p.request?.refCode || "—"}</span>
                    <span className="text-[10px] text-ink-500">·</span>
                    <span className="text-[10px] font-mono text-ink-600">{p.when}</span>
                    <Chip className={`!text-[10px] !py-0.5 ${STATUS_STYLES[p.status] || ""}`}>
                      {t(`myProposals.status.${p.status}`) || p.status?.toUpperCase()}
                    </Chip>
                  </div>
                  <h3 className="text-sm font-semibold text-ink-900 leading-snug truncate">{p.request?.title || "—"}</h3>
                  {p.excerpt && <p className="text-xs text-ink-700 mt-1.5 line-clamp-2">{p.excerpt}</p>}
                  <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-ink-600">
                    {p.proposedPrice != null && <span className="font-mono">{t("proposal.price")}: € {Number(p.proposedPrice).toLocaleString("it-IT")}</span>}
                    {p.proposedDays != null && <span className="font-mono">{t("proposal.days")}: {p.proposedDays}</span>}
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button onClick={() => navigate(`/app/requests/${p.request?.refCode}`)} data-testid={`my-prop-open-req-${p.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-carbon-600 hover:border-electric-500 text-xs text-ink-800 hover:text-electric-600 transition">
                    {t("myProposals.openRequest")} <ChevronRight className="w-3 h-3" />
                  </button>
                  <button onClick={() => openChat(p)} data-testid={`my-prop-chat-${p.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-carbon-600 hover:border-electric-500 text-xs text-ink-800 hover:text-electric-600 transition">
                    <MessageSquare className="w-3 h-3" /> {t("myProposals.chat")}
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
